/**
 * Validation utilities for sanitizing and validating user inputs
 */

/**
 * Sanitizes a string for use in API calls by trimming and limiting length
 */
export function sanitizeInput(input: string, maxLength: number = 100): string {
  if (typeof input !== 'string') {
    return '';
  }
  return input.trim().slice(0, maxLength);
}

/**
 * Validates a cocktail ID - should be numeric
 */
export function validateCocktailId(id: string | undefined): string | null {
  if (!id || typeof id !== 'string') {
    return null;
  }
  // TheCocktailDB uses numeric IDs
  const trimmed = id.trim();
  if (!/^\d+$/.test(trimmed) || trimmed.length > 20) {
    return null;
  }
  return trimmed;
}

/**
 * Validates a search query
 */
export function validateSearchQuery(query: string | null | undefined): string | null {
  if (!query || typeof query !== 'string') {
    return null;
  }
  const sanitized = sanitizeInput(query, 100);
  if (sanitized.length === 0) {
    return null;
  }
  return sanitized;
}

/**
 * Validates a browse mode parameter
 */
export function validateBrowseMode(mode: string | null): 'letter' | 'category' | 'glass' | 'alcoholic' | null {
  if (!mode || typeof mode !== 'string') {
    return null;
  }
  const validModes: Array<'letter' | 'category' | 'glass' | 'alcoholic'> = ['letter', 'category', 'glass', 'alcoholic'];
  return validModes.includes(mode as any) ? (mode as any) : null;
}

/**
 * Validates a letter parameter (single uppercase letter)
 */
export function validateLetter(letter: string | null): string | null {
  if (!letter || typeof letter !== 'string') {
    return null;
  }
  const trimmed = letter.trim().toUpperCase();
  if (trimmed.length === 1 && /^[A-Z]$/.test(trimmed)) {
    return trimmed;
  }
  return null;
}

/**
 * Validates a browse value parameter (category, glass, alcoholic type, or letter)
 */
export function validateBrowseValue(value: string | null, maxLength: number = 100): string | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const sanitized = sanitizeInput(value, maxLength);
  if (sanitized.length === 0) {
    return null;
  }
  return sanitized;
}
